import React, { useContext } from "react";
import styled from "styled-components";
import { List, ListItem } from "@mui/material";
import { Text } from "src/components";
import ListAvatar from "src/scenes/Chat/components/ListAvatar";
import ListText from "src/scenes/Chat/components/children/ListText";
import { UserContext } from "src/scenes/App/UserContext";
import UnreadCount from "src/scenes/Chat/components/children/UnreadCount";
import { ChatListScrollableContainer } from "src/scenes/Chat/components/styled/chatListStyled";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { isUserOnline } from "src/utils/helpers";
import { getUnreadCountWithoutCallNotes } from "src/scenes/Chat/utils/unreadNotifications";

export const StyledText = styled(Text)`
    font-weight: ${ p => p.weight || 400 };
    color: ${ p => p.color || "var(--calendar-text, #171A22)" };
    text-align: left;
    font-size: ${ p => p.size };
    margin: ${ p => p.margin || "1px" };
    text-align: ${ p => p.align || "left"};
`;

const ChatListItem = styled(ListItem)`
    background-color: ${p => p.$isActive ? "var(--calendar-surface-secondary, #F9FAFC)" : "transparent"};
    border: 1px solid ${p => p.$isActive ? "var(--calendar-border, #DBE5ED)" : "transparent"};
    border-radius: 0.625rem;
    cursor: ${p => p.$isActive ? "default" : "pointer"};
`;

const CenteredContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ListOfChats = (props) => {
    const {
        matrixClient,
        currentMatrixUserId,
        useChatMessage: ctxUseChatMessage,
        //TODO: usePresenceListener: ctxUsePresenceListener,
        useMatrixChatList: ctxUseMatrixChatList,
    } = useContext(ChatContext);

    const { openedMatrixChat, setOpenedMatrixChat } = ctxUseChatMessage;
    //TODO: const { onlineUsersMatrixId } = ctxUsePresenceListener;
    const { upsertRoomInMatrixChatList } = ctxUseMatrixChatList;
    const { searchedChatList, setLookingForLeadershipToChat } = props;
    const availableChats = Array.isArray(searchedChatList) ? searchedChatList : [];
    
    const currentUser = useContext(UserContext);

    const isChatActive = (uuid) => openedMatrixChat && openedMatrixChat.uuid === uuid;

    const runExternalCallbacks = (chat) => {
        setOpenedMatrixChat(chat);
        setLookingForLeadershipToChat(null);
    };

    const beforeOpeningChat = async (chat) => {
        runExternalCallbacks(chat);

        //run this silently
        if (chat.matrixRoomUnreadNotificationCount !== 0 && chat.matrixClient) {
            const chatMatrixClient = chat.matrixClient;
            const room = chatMatrixClient.getRoom(chat.matrixRoomId);
            if (!room) {
                return;
            }
            const lastEvent = room.getLastLiveEvent();
            if (!lastEvent) {
                return;
            }
            await chatMatrixClient.sendReadReceipt(lastEvent);
            upsertRoomInMatrixChatList(chat.matrixRoomId);
        }
    };

    const renderChatsList = (givenSearchedChatsList) => {
        return givenSearchedChatsList.map(chat => {
            const room = matrixClient?.getRoom?.(chat.matrixRoomId);
            const liveUnreadCount = getUnreadCountWithoutCallNotes(room, currentMatrixUserId || "");
            const { isGroupChat, users, name } = chat;
            const { matrixRoomLoading, matrixRoomLatestEvent, matrixRoomRecentMessage, matrixRoomUnreadNotificationCount } = chat;

            const otherChatUser = users.find(user => user.matrixUserId !== currentMatrixUserId) ?? null;
            const isOtherUserOnline = otherChatUser && matrixClient
                ? isUserOnline(matrixClient, otherChatUser.matrixUserId)
                : false;

            return (
                <ChatListItem custom-attribute-key={chat.uuid} key={chat.uuid}
                    $isActive={isChatActive(chat.uuid)}
                    style={{ boxSizing: "border-box", padding: "0.625rem", borderRadius: "0.625rem", display: "flex" }}
                    onClick={() => beforeOpeningChat(chat)}
                >
                    <ListAvatar
                        currentUser={currentUser}
                        users={users}
                        isGroupChat={isGroupChat}
                        currentMatrixUserId={currentMatrixUserId}
                        isOnline={ isOtherUserOnline }
                        showPresenceStatus={true} //TODO: use with `onlineUsersMatrixId` or use `{ isUserOnline } from "src/utils/helpers";`
                    />

                    <>
                        <ListText 
                            matrixRoomLoading={matrixRoomLoading} 
                            chatName={name} 
                            latestEvent={matrixRoomLatestEvent} 
                            recentMessage={matrixRoomRecentMessage} 
                            showTimestamp={matrixRoomUnreadNotificationCount === 0} 
                            chat={chat}
                        />
                        <div>{ liveUnreadCount > 0 && <UnreadCount unreadCount={liveUnreadCount} /> }</div>
                    </> 
                           
                </ChatListItem>
            );
        });
    };

    return (
        <React.Fragment>
            <>
                { availableChats.length > 0
                    ?
                    <ChatListScrollableContainer>
                        <List sx={{ padding: 0 }} >{ renderChatsList(availableChats) }</List>
                    </ChatListScrollableContainer>
                    :
                    <CenteredContent style={{ height: "100%" }}>
                        <StyledText align="center" margin="1.25rem 0">You have no available chats.</StyledText>
                    </CenteredContent>
                }
            </>
        </React.Fragment>
    );
};

export default ListOfChats;
